/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */

import './style/viewer.css';

import { keepAspectRatio } from './lib/dom.js';
import { Glossary } from './lib/glossary.js';
import { setLanguage } from './lib/i18n.js';
import { linksToMediaPlayer } from './lib/media.js';
import { Social } from './lib/social.js';

/*
 * The per-point page.
 *
 * `/<map_id>/popup/<id>` stays a server-rendered page: it exists so a
 * crawler can index one map point on its own, and a browser that lands on
 * it is sent back to the map. It is not part of the Map Viewer,
 * but it needs a little of the same behaviour, and this is that little.
 *
 * It replaces about fifty lines of script that were written inline in the
 * template, which meant jQuery, jQuery UI and XRegExp were loaded for a
 * page that shows one paragraph of text.
 */

/** Read the page's configuration island. */
function readConfig() {
    const element = document.getElementById('popup-config');
    try {
        return JSON.parse(element?.textContent ?? '{}');
    } catch (error) {
        console.error(`Error parsing popup configuration: ${error}`);
        return {};
    }
}

/**
 * Keep embedded media inside the column it sits in.
 *
 * Authors paste `width` and `height` from wherever they copied the embed,
 * and those are usually larger than the page. The ratio is kept and the
 * attributes dropped, so the element scales instead of overflowing.
 *
 * @param {HTMLElement} container - The content column.
 */
function sizeEmbeddedMedia(container) {
    container.querySelectorAll('iframe, img').forEach((element) => {
        const width = Number.parseInt(element.getAttribute('width'), 10) || 16;
        const height = Number.parseInt(element.getAttribute('height'), 10) || 9;
        keepAspectRatio(element, width / height, 'width', container);
        element.removeAttribute('width');
        element.removeAttribute('height');
    });
}

/**
 * Point the share links at the page a reader would actually send.
 *
 * The point page redirects a browser back to the map, so sharing its own
 * URL would send a recipient somewhere that immediately bounces them. The
 * top frame's URL is the one worth sharing.
 *
 * @param {object} services - Which services this map offers.
 */
function setUpSharing(services) {
    const social = new Social(services || {});
    let href = window.location.href;
    try {
        href = window.top.location.href;
    } catch {
        // Cross-origin embed: the frame's own URL is the best available.
    }

    document.querySelectorAll('.social-share-link').forEach((link) => {
        const service = link.dataset.serviceId;
        const url = social.getServiceLinkUrl(
            service,
            href,
            link.dataset.shareTitle,
            link.dataset.shareText
        );
        if (!url) return;
        link.setAttribute('href', url);
        link.setAttribute('target', service === 'email' ? '_top' : '_blank');
    });
}

/** Set the page up. */
export async function init() {
    const config = readConfig();
    setLanguage(config.lang || document.documentElement.lang);

    const content = document.querySelector('.popup > section > main');
    if (content) {
        linksToMediaPlayer(content);
        sizeEmbeddedMedia(content);
    }

    setUpSharing(config.social);

    const body = document.querySelector('.popup > main');
    if (body && config.glossaryUrl) {
        const glossary = new Glossary();
        await glossary.load(config.glossaryUrl);
        glossary.linkTerms(body);
    }
}

document.addEventListener('DOMContentLoaded', () => {
    init();
});
